import React, { useState, useEffect, useRef } from "react";
import {
  getWebsiteSettings,
  saveWebsiteSettings,
} from "../services/azureWebsiteService";
import {
  uploadElGatoMuertoImage,
  deleteElGatoMuertoImage,
  validateElGatoImageFile,
} from "../services/azureElGatoMuertoBlobService";
import { isElGatoOnly } from "../utils/permissions";

const emptyTermin = () => ({
  datum: "",
  programm: "",
  link: "",
  oeffnungVon: "",
  oeffnungBis: "",
  host: "",
});

const isValidUrl = (value) => {
  if (!value) return true;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

const validateTermine = (termine) => {
  for (let i = 0; i < termine.length; i++) {
    const t = termine[i];
    if (!isValidUrl(t.link)) {
      return `Termin ${i + 1}: Link ist keine gültige URL.`;
    }
    if (t.oeffnungVon && t.oeffnungBis && t.oeffnungBis <= t.oeffnungVon) {
      return `Termin ${i + 1}: Öffnungszeit "bis" muss nach "von" liegen.`;
    }
  }
  return null;
};

const validateImages = (images) => {
  for (let i = 0; i < images.length; i++) {
    if (!images[i].altText || !images[i].altText.trim()) {
      return `Bild ${i + 1}: Alt-Text ist Pflicht (Barrierefreiheit).`;
    }
  }
  return null;
};

// Die Schrift der El-Gato-Muerto-Website kann keine Zeichen ausserhalb
// von ASCII darstellen (Umlaute, ß, Akzente) — daher nur Warnung, kein Fehler.
const hasNonAscii = (value) =>
  [...(value || "")].some((ch) => ch.codePointAt(0) > 127);

const collectSpecialCharWarnings = (formData) => {
  const warnings = [];
  if (hasNonAscii(formData.elGatoMuertoTextOben || "")) {
    warnings.push("Text oben");
  }
  formData.elGatoMuerto.forEach((termin, i) => {
    if (hasNonAscii(termin.programm || "")) {
      warnings.push(`Termin ${i + 1} (Programm)`);
    }
    if (hasNonAscii(termin.host || "")) {
      warnings.push(`Termin ${i + 1} (Host)`);
    }
    if (hasNonAscii(termin.link || "")) {
      warnings.push(`Termin ${i + 1} (Link)`);
    }
  });
  formData.elGatoMuertoImages.forEach((img, i) => {
    if (hasNonAscii(img.altText || "")) {
      warnings.push(`Bild ${i + 1} (Alt-Text)`);
    }
  });
  if (hasNonAscii(formData.elGatoMuertoTextUnten || "")) {
    warnings.push("Text unten");
  }
  return warnings;
};

const WebsiteSettings = ({ user } = {}) => {
  // Externe El-Gato-Muerto-Person sieht ausschliesslich den El-Gato-Muerto-Block.
  const elGatoOnly = isElGatoOnly(user);
  const [formData, setFormData] = useState({
    about: "",
    contact: {
      adresse: "",
      email: "",
      telefon: "",
      instagram: "",
    },
    impressum: "",
    danke: [],
    elGatoMuerto: [],
    elGatoMuertoImages: [],
    elGatoMuertoTextOben: "",
    elGatoMuertoTextUnten: "",
  });
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getWebsiteSettings();
      setFormData(data);
    } catch (err) {
      setError("Laden fehlgeschlagen: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const terminError = validateTermine(formData.elGatoMuerto);
    if (terminError) {
      setError(terminError);
      return;
    }
    const imageError = validateImages(formData.elGatoMuertoImages);
    if (imageError) {
      setError(imageError);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await saveWebsiteSettings(formData);
      alert("Erfolgreich gespeichert");
    } catch (err) {
      setError("Speichern fehlgeschlagen: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddName = () => {
    if (newName.trim()) {
      setFormData({
        ...formData,
        danke: [...formData.danke, newName.trim()],
      });
      setNewName("");
    }
  };

  const handleRemoveName = (index) => {
    setFormData({
      ...formData,
      danke: formData.danke.filter((_, i) => i !== index),
    });
  };

  // --- El Gato Muerto: Termine ---
  const handleAddTermin = () => {
    setFormData({
      ...formData,
      elGatoMuerto: [...formData.elGatoMuerto, emptyTermin()],
    });
  };

  const handleRemoveTermin = (index) => {
    if (!window.confirm("Termin wirklich entfernen?")) return;
    setFormData({
      ...formData,
      elGatoMuerto: formData.elGatoMuerto.filter((_, i) => i !== index),
    });
  };

  const handleTerminChange = (index, field, value) => {
    const updated = formData.elGatoMuerto.map((t, i) =>
      i === index ? { ...t, [field]: value } : t,
    );
    setFormData({ ...formData, elGatoMuerto: updated });
  };

  // --- El Gato Muerto: Bilder ---
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Erst alle Dateien validieren, bevor irgendetwas hochgeladen wird.
    for (const file of files) {
      try {
        validateElGatoImageFile(file);
      } catch (err) {
        setError(`Datei "${file.name}": ${err.message}`);
        event.target.value = "";
        return;
      }
    }

    setError(null);
    setUploadingImages(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const img = await uploadElGatoMuertoImage(file);
        uploaded.push(img);
      }
      setFormData((prev) => ({
        ...prev,
        elGatoMuertoImages: [...prev.elGatoMuertoImages, ...uploaded],
      }));
    } catch (err) {
      setError("Upload fehlgeschlagen: " + err.message);
    } finally {
      setUploadingImages(false);
      event.target.value = "";
    }
  };

  const handleRemoveImage = async (index) => {
    if (!window.confirm("Bild wirklich entfernen?")) return;
    const img = formData.elGatoMuertoImages[index];
    try {
      await deleteElGatoMuertoImage(img.blobName);
      setFormData((prev) => ({
        ...prev,
        elGatoMuertoImages: prev.elGatoMuertoImages.filter(
          (_, i) => i !== index,
        ),
      }));
    } catch (err) {
      setError("Bild löschen fehlgeschlagen: " + err.message);
    }
  };

  const handleAltTextChange = (index, value) => {
    const updated = formData.elGatoMuertoImages.map((img, i) =>
      i === index ? { ...img, altText: value } : img,
    );
    setFormData({ ...formData, elGatoMuertoImages: updated });
  };

  if (loading) return <div className="website-settings">Lädt...</div>;

  const specialCharWarnings = collectSpecialCharWarnings(formData);

  return (
    <div className="website-settings">
      {error && <div className="error-message">{error}</div>}

      {!elGatoOnly && (
        <>
      <div className="form-section">
        <h2>About</h2>
        <textarea
          value={formData.about}
          onChange={(e) => setFormData({ ...formData, about: e.target.value })}
          placeholder="400-800 Wörter Text"
          rows={12}
        />
      </div>

      <div className="form-section">
        <h2>Contact</h2>
        <textarea
          placeholder="Adresse"
          value={formData.contact.adresse}
          onChange={(e) =>
            setFormData({
              ...formData,
              contact: { ...formData.contact, adresse: e.target.value },
            })
          }
          rows={4}
        />

        <input
          type="email"
          placeholder="Email"
          value={formData.contact.email}
          onChange={(e) =>
            setFormData({
              ...formData,
              contact: { ...formData.contact, email: e.target.value },
            })
          }
        />
        <input
          type="tel"
          placeholder="Telefon"
          value={formData.contact.telefon}
          onChange={(e) =>
            setFormData({
              ...formData,
              contact: { ...formData.contact, telefon: e.target.value },
            })
          }
        />
        <input
          type="url"
          placeholder="Instagram URL"
          value={formData.contact.instagram}
          onChange={(e) =>
            setFormData({
              ...formData,
              contact: { ...formData.contact, instagram: e.target.value },
            })
          }
        />
      </div>

      <div className="form-section">
        <h2>Impressum</h2>
        <textarea
          value={formData.impressum}
          onChange={(e) =>
            setFormData({ ...formData, impressum: e.target.value })
          }
          placeholder="Impressum Text"
          rows={8}
        />
      </div>

      <div className="form-section">
        <h2>Danke</h2>
        <div className="name-input-group">
          <input
            type="text"
            placeholder="Name hinzufügen"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleAddName()}
          />
          <button onClick={handleAddName} className="btn">
            Hinzufügen
          </button>
        </div>
        <ul className="names-list">
          {formData.danke.map((name, index) => (
            <li key={index}>
              {name}
              <button onClick={() => handleRemoveName(index)}>×</button>
            </li>
          ))}
        </ul>
      </div>
        </>
      )}

      <div className="form-section el-gato-muerto">
        <h2>El Gato Muerto</h2>

        {specialCharWarnings.length > 0 && (
          <div className="warning-message">
            Achtung: Sonderzeichen wie Umlaute (ä, ö, ü), ß oder Akzente
            können in der Schrift der El-Gato-Muerto-Website nicht
            dargestellt werden. Betroffen: {specialCharWarnings.join(", ")}
          </div>
        )}

        <h3>Text oben</h3>
        <textarea
          value={formData.elGatoMuertoTextOben}
          onChange={(e) =>
            setFormData({
              ...formData,
              elGatoMuertoTextOben: e.target.value,
            })
          }
          placeholder="Text oberhalb der Termine"
          rows={4}
        />

        <h3>Termine</h3>
        {formData.elGatoMuerto.length === 0 && (
          <p className="empty-hint">Noch keine Termine angelegt.</p>
        )}
        <ul className="termine-list">
          {formData.elGatoMuerto.map((termin, index) => (
            <li key={index} className="termin-item">
              <div className="termin-grid">
                <label>
                  <span>Datum</span>
                  <input
                    type="date"
                    value={termin.datum || ""}
                    onChange={(e) =>
                      handleTerminChange(index, "datum", e.target.value)
                    }
                  />
                </label>
                <label>
                  <span>Programm</span>
                  <input
                    type="text"
                    value={termin.programm || ""}
                    onChange={(e) =>
                      handleTerminChange(index, "programm", e.target.value)
                    }
                  />
                </label>
                <label>
                  <span>Host</span>
                  <input
                    type="text"
                    value={termin.host || ""}
                    onChange={(e) =>
                      handleTerminChange(index, "host", e.target.value)
                    }
                  />
                </label>
                <label>
                  <span>Link</span>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={termin.link || ""}
                    onChange={(e) =>
                      handleTerminChange(index, "link", e.target.value)
                    }
                  />
                </label>
                <label>
                  <span>Öffnung von</span>
                  <input
                    type="time"
                    value={termin.oeffnungVon || ""}
                    onChange={(e) =>
                      handleTerminChange(index, "oeffnungVon", e.target.value)
                    }
                  />
                </label>
                <label>
                  <span>Öffnung bis</span>
                  <input
                    type="time"
                    value={termin.oeffnungBis || ""}
                    onChange={(e) =>
                      handleTerminChange(index, "oeffnungBis", e.target.value)
                    }
                  />
                </label>
              </div>
              <button
                type="button"
                className="btn btn-remove"
                onClick={() => handleRemoveTermin(index)}
              >
                Termin entfernen
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="btn" onClick={handleAddTermin}>
          Termin hinzufügen
        </button>

        <h3>Bilder</h3>
        <div className="image-upload">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handleImageUpload}
            disabled={uploadingImages}
          />
          {uploadingImages && (
            <span className="upload-status">Lade hoch…</span>
          )}
        </div>
        {formData.elGatoMuertoImages.length === 0 && (
          <p className="empty-hint">Noch keine Bilder hochgeladen.</p>
        )}
        <ul className="image-list">
          {formData.elGatoMuertoImages.map((img, index) => (
            <li key={img.blobName} className="image-item">
              <img src={img.url} alt={img.altText || ""} />
              <div className="image-meta">
                <label>
                  <span>Alt-Text (Pflicht)</span>
                  <input
                    type="text"
                    value={img.altText || ""}
                    onChange={(e) =>
                      handleAltTextChange(index, e.target.value)
                    }
                    placeholder="Beschreibung für Barrierefreiheit"
                  />
                </label>
                <button
                  type="button"
                  className="btn btn-remove"
                  onClick={() => handleRemoveImage(index)}
                >
                  Bild entfernen
                </button>
              </div>
            </li>
          ))}
        </ul>

        <h3>Text unten</h3>
        <textarea
          value={formData.elGatoMuertoTextUnten}
          onChange={(e) =>
            setFormData({
              ...formData,
              elGatoMuertoTextUnten: e.target.value,
            })
          }
          placeholder="Text unterhalb der Bilder"
          rows={4}
        />
      </div>

      <button
        onClick={handleSave}
        className="btn btn-primary"
        disabled={saving || uploadingImages}
      >
        {saving ? "Speichert..." : "Speichern"}
      </button>
    </div>
  );
};

export default WebsiteSettings;
